import { Button, ButtonProps, SxProps } from "@mui/material";
import { Theme } from "@emotion/react";
import ImageComponent from "components/image/ImageComponent";
import { useMemo } from "react";

interface ButtonComponentProps extends ButtonProps {
  src?: string;
  label?: string;
  onClick?: () => void;
  sx?: SxProps<Theme>;
  sxImage?: SxProps<Theme>;
  children?: React.ReactNode;
  theme?: "default" | "dark";
}

/**
 * ButtonComponent component renders a button with an icon and label.
 * @param {Object} props - The properties object.
 * @param {string} props.label - The text label to display on the button.
 * @param {Function} props.onClick - The callback function to handle the button click event.
 * @param {SxProps<Theme>} props.sx - The MUI sx prop for styling.
 */
function ButtonComponent({
  label,
  src,
  sxImage,
  sx = {},
  children,
  theme = "default",
  variant,
  ...rest
}: ButtonComponentProps) {
  const themeStyle = useMemo(() => {
    switch (theme) {
      case "dark":
        return {
          color: "var(--white)",
          backgroundColor: "var(--gray-500)",
          ":hover": {
            backgroundColor: "var(--gray-250)",
            boxShadow: "var(--shadow)",
          },
        };

      default:
        return {
          color: "var(--gray-700)",
          backgroundColor: "var(--white)",
          borderRadius: '4px',
          border: "1px solid var(--gray-250)",
          ":hover": {
            opacity: 0.8,
            boxShadow: "var(--shadow)",
          },
        };
    }
  }, [theme]);

  return (
    <Button
      {...rest}
      variant={variant || (theme === "dark" ? "contained" : "outlined")}
      sx={{
        height: "50px",
        width: "180px",
        fontSize: { xs: 16, md: 18, lg: 23 },
        fontWeight: "medium",
        borderRadius: "9px",
        ...themeStyle,
        ...sx,
      }}
    >
      {src ? <ImageComponent src={src} sx={sxImage} /> : null}
      {label}
      {children}
    </Button>
  );
}

export default ButtonComponent;
